import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { billingIsLocked } from '../lib/billing';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingLocked, setBillingLocked] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) console.error('fetchProfile error:', error);
    setProfile(data);
    setLoading(false);
    if (data) checkBilling(data);
  }

  async function checkBilling(prof) {
    const role = prof?.role;
    if (!prof?.id) { setBillingLocked(false); return; }
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();

    if (role === 'athlete') {
      const { data } = await supabase
        .from('billing')
        .select('paid_at')
        .eq('athlete_id', prof.id)
        .eq('month', month)
        .eq('year', year)
        .maybeSingle();
      setBillingLocked(billingIsLocked(data));
    } else if (role === 'parent') {
      // Parent is locked if ANY linked athlete is locked
      const { data: links } = await supabase
        .from('parent_athlete_links')
        .select('athlete_id')
        .eq('parent_id', prof.id);
      if (!links?.length) { setBillingLocked(false); return; }
      const ids = links.map(l => l.athlete_id);
      const { data: bills } = await supabase
        .from('billing')
        .select('athlete_id, paid_at')
        .in('athlete_id', ids)
        .eq('month', month)
        .eq('year', year);
      const anyLocked = ids.some(id => {
        const bill = (bills ?? []).find(b => b.athlete_id === id);
        return billingIsLocked(bill);
      });
      setBillingLocked(anyLocked);
    } else {
      setBillingLocked(false);
    }
  }

  async function refreshBilling() {
    if (profile) await checkBilling(profile);
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signUp(email, password, fullName, role) {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, role },
      },
    });
    return { error };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  }

  async function refreshProfile() {
    if (user) await fetchProfile(user.id);
  }

  const value = {
    user,
    profile,
    loading,
    billingLocked,
    signIn,
    signUp,
    signOut,
    resetPassword,
    refreshProfile,
    refreshBilling,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
